import React, { useEffect, useState } from 'react';

import { Trans } from 'react-i18next';

import ApproverRulesBlockedList from './ApproverRulesBlockedList';
import { DANGER_BUTTON, SOFT_BUTTON } from './organisationUserFormat';

import Button from 'CommonComponents/Button';
import Modal from 'CommonComponents/Modal';
import ModalHeader from 'CommonComponents/ModalHeader';

import useModal from 'Hooks/useModal';

/**
 * "Remove from project?" for one of the member's projects. `removal` is
 * {user, project, check} with check as the server's removal preflight; null
 * keeps the dialog closed. Approval rules naming the user block the removal;
 * removing the only Event Admin is allowed once warned.
 */
export default function RemoveFromProjectModal({ removal, onClose, onConfirm }) {
    const modal = useModal();
    const [removing, setRemoving] = useState(false);
    const { show, hide } = modal;

    useEffect(() => {
        if (removal) {
            setRemoving(false);
            show();
        } else {
            hide();
        }
    }, [removal, show, hide]);

    const blocked = (removal?.check.rules ?? []).length > 0;

    const confirm = () => {
        setRemoving(true);
        Promise.resolve(onConfirm(removal)).finally(() => setRemoving(false));
    };

    return (
        <Modal modal={modal} modalIndexClass="tw-z-[110]" parentContainerClasses="tw-max-w-lg">
            {removal && (
                <>
                    <ModalHeader title={$t('Remove from project?')} close={onClose} />
                    <div className="tw-space-y-4 tw-text-0.5sm tw-text-charcoal">
                        <p className="tw-mb-0">
                            <Trans
                                i18nKey="{{name}} will be removed from <1>{{project}}</1>."
                                values={{ name: removal.user.name, project: removal.project.name }}
                                components={{ 1: <strong className="tw-font-semibold" /> }}
                            />
                        </p>

                        {blocked ? (
                            <ApproverRulesBlockedList rules={removal.check.rules} linkUrl={removal.check.link_url} />
                        ) : (
                            <>
                                <p className="tw-mb-0 tw-rounded-lg tw-border-s-4 tw-border-amber-500 tw-bg-amber-50 tw-px-4 tw-py-3">
                                    {$t(
                                        'Project-level settings (role, departments, venues, cost centres and modules) will be deleted. If the user is added back later, these will need to be set up again.',
                                    )}
                                </p>
                                {removal.check.is_last_event_admin && (
                                    <p className="tw-mb-0 tw-font-medium tw-text-red">
                                        {$t('{{name}} is the only Event Admin of this project. The project will have no Event Admin after they are removed.', {
                                            name: removal.user.name,
                                        })}
                                    </p>
                                )}
                            </>
                        )}

                        <div className="modal__footer">
                            <Button variant="secondary" className={SOFT_BUTTON} onClick={onClose} disabled={removing}>
                                {$t('Cancel')}
                            </Button>
                            <Button variant="danger" className={DANGER_BUTTON} onClick={confirm} disabled={blocked || removing} loading={removing}>
                                {$t('Remove from project')}
                            </Button>
                        </div>
                    </div>
                </>
            )}
        </Modal>
    );
}
